/**
 * Discord Prospects — admin view of the Discord prospecting pipeline (the
 * Discord-channel counterpart of the Onboarding Pipeline). Discovery + scoring
 * are automated (daily cron); this screen is for working the ranked queue:
 * review qualified servers, run an on-demand scrape, and advance status.
 *
 * Outreach itself is deliberately NOT automated here — Discord bans bulk DMs,
 * so a human sends each touch through an allowed channel. This screen gives
 * them the ranked, enriched worklist to do it from.
 */
import { useMemo, useState } from 'react';
import { ExternalLink } from 'lucide-react';
import {
  DISCORD_PROSPECT_STATUSES,
  DiscordProspectListParams,
  DiscordProspectStatus
} from '@/api/discordProspects';
import {
  useDiscordConfig,
  useDiscordOverview,
  useDiscordProspects,
  useRescoreDiscordProspects,
  useRunDiscordScrape,
  useUpdateDiscordProspect
} from '@/hooks/useDiscordProspects';
import {
  ActionButton,
  Card,
  Pagination,
  SectionLabel,
  Select,
  Skeleton,
  StatCard
} from '@/components/ui/atoms';
import { fmtDate, fmtNum } from '@/lib/format';

const STATUS_OPTIONS: Array<{ value: DiscordProspectStatus | ''; label: string }> =
  [
    { value: '', label: 'All statuses' },
    ...DISCORD_PROSPECT_STATUSES.map(s => ({
      value: s,
      label: s.charAt(0).toUpperCase() + s.slice(1)
    }))
  ];

const UB_OPTIONS: Array<{ value: 'true' | 'false' | ''; label: string }> = [
  { value: '', label: 'UnbelievaBoat: any' },
  { value: 'true', label: 'Runs UnbelievaBoat' },
  { value: 'false', label: 'No UnbelievaBoat' }
];

const SCORE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '', label: 'Any score' },
  { value: '45', label: '≥ 45 (qualified)' },
  { value: '60', label: '≥ 60' },
  { value: '75', label: '≥ 75' }
];

const SORT_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '-score', label: 'Score (high→low)' },
  { value: 'score', label: 'Score (low→high)' },
  { value: '-memberCount', label: 'Members (high→low)' },
  { value: '-createdAt', label: 'Newest first' }
];

export function DiscordProspectsScreen() {
  const overview = useDiscordOverview();
  const config = useDiscordConfig();

  // ── Filters ──
  const [status, setStatus] = useState<DiscordProspectStatus | ''>('qualified');
  const [ub, setUb] = useState<'true' | 'false' | ''>('');
  const [minScore, setMinScore] = useState<string>('');
  const [sort, setSort] = useState<string>('-score');
  const [q, setQ] = useState<string>('');
  const [page, setPage] = useState<number>(1);

  // ── Scrape panel ──
  const [tagsInput, setTagsInput] = useState<string>('');
  const [maxResults, setMaxResults] = useState<string>('25');

  const params: DiscordProspectListParams = useMemo(
    () => ({
      page,
      limit: 25,
      status,
      usesUnbelievaboat: ub,
      minScore: minScore === '' ? '' : Number(minScore),
      sort,
      q: q.trim()
    }),
    [page, status, ub, minScore, sort, q]
  );

  const list = useDiscordProspects(params);
  const scrape = useRunDiscordScrape();
  const rescore = useRescoreDiscordProspects();
  const update = useUpdateDiscordProspect();

  const o = overview.data;
  const cfg = config.data;
  const enabled = cfg?.enabled ?? o?.enabled ?? false;
  const live = cfg?.apify.mode === 'live';

  const runScrape = (dryRun: boolean) => {
    const tags = tagsInput
      .split(',')
      .map(t => t.trim())
      .filter(Boolean);
    scrape.mutate({
      dryRun,
      tags: tags.length ? tags : undefined,
      maxResults: Number(maxResults) || 25
    });
  };

  const docs = list.data?.docs ?? [];
  const totalPages = list.data?.totalPages ?? 1;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-nurev-text">Discord Prospects</h1>
        <p className="mt-1 text-sm text-nurev-muted">
          Economy-bot communities discovered from Discord&apos;s server
          directory, ranked by fit. Discovery + scoring run daily; work the
          queue here. Outreach is human-sent — no bulk DMs.
        </p>
      </div>

      {/* ── KPIs ── */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard
          label="Total prospects"
          value={fmtNum(o?.totalProspects)}
          loading={overview.isLoading}
        />
        <StatCard
          label="Qualified +"
          value={fmtNum(o?.qualifiedOrBeyond)}
          color="text-amber-300"
          loading={overview.isLoading}
        />
        <StatCard
          label="Run UnbelievaBoat"
          value={fmtNum(o?.usingUnbelievaboat)}
          loading={overview.isLoading}
        />
        <StatCard
          label="Pipeline"
          value={enabled ? 'Enabled' : 'Inert'}
          sub={live ? 'Apify: live' : 'Apify: stub'}
          color={enabled ? 'text-emerald-300' : 'text-nurev-muted'}
          loading={config.isLoading}
        />
      </div>

      {/* ── Scrape panel ── */}
      <Card>
        <SectionLabel>Run a scrape</SectionLabel>
        {!enabled && (
          <p className="mb-3 rounded-lg bg-amber-500/10 px-3 py-2 text-xs text-amber-300 ring-1 ring-amber-500/20">
            The pipeline is inert — set <code>DISCORD_PROSPECT_ENABLED=true</code>{' '}
            (and <code>APIFY_API_TOKEN</code>) in the API secret to enable live
            scraping. The daily cron is also gated on this.
          </p>
        )}
        <div className="flex flex-col gap-3 lg:flex-row lg:items-end">
          <div className="flex-1">
            <label className="mb-1 block text-[10px] uppercase tracking-wider text-nurev-muted">
              Search terms (comma-separated)
            </label>
            <input
              value={tagsInput}
              onChange={e => setTagsInput(e.target.value)}
              placeholder={
                cfg?.tags?.length
                  ? cfg.tags.join(', ')
                  : 'economy, currency, casino'
              }
              className="w-full rounded-lg border border-nurev-border bg-white/[0.04] px-3 py-1.5 text-sm text-nurev-text focus:border-amber-500/50 focus:outline-none"
            />
          </div>
          <div className="w-28">
            <label className="mb-1 block text-[10px] uppercase tracking-wider text-nurev-muted">
              Max / term
            </label>
            <input
              type="number"
              value={maxResults}
              onChange={e => setMaxResults(e.target.value)}
              className="w-full rounded-lg border border-nurev-border bg-white/[0.04] px-3 py-1.5 text-sm text-nurev-text focus:border-amber-500/50 focus:outline-none"
            />
          </div>
          <div className="flex gap-2">
            <ActionButton
              onClick={() => runScrape(false)}
              disabled={!enabled || scrape.isPending}
            >
              {scrape.isPending ? 'Scraping…' : 'Run scrape'}
            </ActionButton>
            <ActionButton
              variant="neutral"
              onClick={() => runScrape(true)}
              disabled={!enabled || scrape.isPending}
            >
              Dry run
            </ActionButton>
            <ActionButton
              variant="neutral"
              onClick={() => rescore.mutate()}
              disabled={rescore.isPending}
            >
              {rescore.isPending ? 'Re-scoring…' : 'Re-score'}
            </ActionButton>
          </div>
        </div>
        {scrape.data && (
          <p className="mt-3 text-xs text-nurev-muted">
            Last run{scrape.data.live ? '' : ' (stub)'}: discovered{' '}
            <span className="text-nurev-text">
              {scrape.data.result.discovered}
            </span>
            , inserted{' '}
            <span className="text-emerald-300">{scrape.data.result.inserted}</span>
            , qualified{' '}
            <span className="text-amber-300">{scrape.data.result.qualified}</span>
            , skipped {scrape.data.result.skippedExisting}.
          </p>
        )}
        {scrape.isError && (
          <p className="mt-3 text-xs text-red-300">
            Scrape failed — check the API logs.
          </p>
        )}
      </Card>

      {/* ── Filters ── */}
      <div className="flex flex-wrap items-center gap-2">
        <Select
          value={status}
          onChange={v => {
            setStatus(v);
            setPage(1);
          }}
          options={STATUS_OPTIONS}
        />
        <Select
          value={ub}
          onChange={v => {
            setUb(v);
            setPage(1);
          }}
          options={UB_OPTIONS}
        />
        <Select
          value={minScore}
          onChange={v => {
            setMinScore(v);
            setPage(1);
          }}
          options={SCORE_OPTIONS}
        />
        <Select value={sort} onChange={setSort} options={SORT_OPTIONS} />
        <input
          value={q}
          onChange={e => {
            setQ(e.target.value);
            setPage(1);
          }}
          placeholder="Search name / tags…"
          className="flex-1 rounded-lg border border-nurev-border bg-white/[0.04] px-3 py-1.5 text-sm text-nurev-text focus:border-amber-500/50 focus:outline-none"
        />
      </div>

      {/* ── Table ── */}
      <Card className="overflow-hidden p-0">
        {list.isLoading ? (
          <div className="space-y-2 p-5">
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : docs.length === 0 ? (
          <p className="p-8 text-center text-sm text-nurev-muted">
            No prospects match. Run a scrape or widen the filters.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[820px] text-sm">
              <thead>
                <tr className="border-b border-nurev-border text-left text-[10px] uppercase tracking-wider text-nurev-muted">
                  <th className="px-4 py-3">Score</th>
                  <th className="px-4 py-3">Server</th>
                  <th className="px-4 py-3">Members</th>
                  <th className="px-4 py-3">Online</th>
                  <th className="px-4 py-3">UB</th>
                  <th className="px-4 py-3">Tags</th>
                  <th className="px-4 py-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {docs.map(p => (
                  <tr
                    key={p._id}
                    className="border-b border-nurev-border/50 hover:bg-white/[0.02]"
                  >
                    <td className="px-4 py-3">
                      <span className="font-bold text-amber-300">
                        {fmtNum(p.score)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="max-w-[260px] truncate text-nurev-text">
                          {p.serverName}
                        </span>
                        {p.inviteUrl && (
                          <a
                            href={p.inviteUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="text-amber-300 hover:text-amber-200"
                            title="Open invite"
                          >
                            <ExternalLink className="h-3.5 w-3.5" />
                          </a>
                        )}
                      </div>
                      {p.description && (
                        <p className="mt-0.5 max-w-[360px] truncate text-[11px] text-nurev-muted">
                          {p.description}
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-3 text-nurev-muted">
                      {fmtNum(p.memberCount)}
                    </td>
                    <td className="px-4 py-3 text-nurev-muted">
                      {fmtNum(p.onlineCount)}
                    </td>
                    <td className="px-4 py-3">
                      {p.usesUnbelievaboat ? (
                        <span className="text-emerald-300">✓</span>
                      ) : (
                        <span className="text-nurev-muted">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex max-w-[200px] flex-wrap gap-1">
                        {(p.tags ?? []).slice(0, 3).map(t => (
                          <span
                            key={t}
                            className="rounded bg-white/[0.06] px-1.5 py-0.5 text-[10px] text-nurev-muted"
                          >
                            {t}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <Select
                        value={p.status}
                        onChange={v =>
                          update.mutate({ id: p._id, input: { status: v } })
                        }
                        options={DISCORD_PROSPECT_STATUSES.map(s => ({
                          value: s,
                          label: s.charAt(0).toUpperCase() + s.slice(1)
                        }))}
                        className="py-1 text-xs"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {totalPages > 1 && (
        <Pagination page={page} totalPages={totalPages} onChange={setPage} />
      )}

      <p className="text-center text-[11px] text-nurev-muted">
        {list.data ? `${fmtNum(list.data.totalDocs)} prospects` : ''}{' '}
        {list.data && (
          <>· updated {fmtDate(docs[0]?.updatedAt)}</>
        )}
      </p>
    </div>
  );
}
